import { useEffect, useRef, useState, type RefObject } from "react";

import * as api from "../../lib/api.js";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { Button, Overlay, cx } from "../ui";
import {
  balanceLine,
  credits,
  defaultPack,
  isBestValue,
  packAfter,
  packHours,
  packPrice,
  planRequired,
  type Wallet,
} from "./wallet";

/**
 * Add credits (#686, #1010): the packs, for a plan holder, and the way to pay for one.
 * Packs only: the plans are their own overlay (`PlansOverlay`), and without a plan Add
 * credits is never offered -- `/account?top_up=1` goes to the plans instead.
 *
 * An overlay over Account: choosing a pack is one task that wants the screen for a few
 * seconds and then hands it back, by leaving for Dodo's checkout, which returns to
 * `/account`. Opened by Add credits, and by `/account?top_up=1` with a plan.
 *
 * Top to bottom, under the header: what is left ("{credits} credits left", or what is owed,
 * in the error colour, when the balance is at or below zero) and that extra credits carry
 * over; the packs; that a meeting continues if credits run out; Compare plans, only when
 * the server suggests a bigger plan; then the button, pinned to the bottom when the content
 * is taller than the screen.
 *
 * Each card has the price on the left and, on the right, the credits, about how many meeting
 * hours the pack buys (rounded down), and what the balance would be after it. The largest,
 * the cheapest per credit, carries a best-value badge. The middle pack opens selected, or,
 * while credits are owed, the smallest that leaves some. The button names the choice:
 * "Buy {credits} credits for {price}". A checkout refused for want of a plan
 * (`billing_plan_required`, #1009) hands over to the plans.
 */
export function TopUpOverlay({
  wallet,
  onClose,
  onComparePlans,
  onPlanRequired = onComparePlans,
}: {
  wallet: Wallet;
  onClose: () => void;
  /** Compare plans, and where a refused checkout goes: the plans overlay. */
  onComparePlans: () => void;
  onPlanRequired?: () => void;
}) {
  const [chosen, setChosen] = useState<string | null>(() => defaultPack(wallet));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [restingPlace, stuck] = useOutOfView();
  const available = wallet.checkout_available;
  const balance = wallet.balance_credits;
  const pack = wallet.packs.find((p) => p.pack === chosen) ?? null;
  // "{credits} credits" with its placeholder left in (an unfilled name stays as written),
  // split around it, so each card can set its number apart in the contract's own words.
  const [creditsBefore, creditsAfter = ""] = t("billing.pack_time").split("{credits}");

  /** Leave for Dodo's checkout; back to /account. */
  async function pay() {
    if (!chosen) return;
    setBusy(true);
    setError("");
    captureAnalytics("checkout_started", { kind: "pack" });
    try {
      const { checkout_url } = await api.checkout(chosen);
      // Left busy: the page is about to go.
      window.location.assign(checkout_url);
    } catch (err) {
      console.warn("checkout failed", err);
      if (planRequired(err)) {
        onPlanRequired();
        return;
      }
      const status = (err as { status?: unknown } | null)?.status;
      setError(status === 503 ? t("billing.unavailable") : t("billing.error_checkout"));
      setBusy(false);
    }
  }

  return (
    // The header label in caps here only: the shared header (`Overlay`) keeps the other
    // overlays' labels as they are. CSS caps, so the string and what is read out keep their
    // case. It is the overlay's only title, so it names the dialog too.
    <Overlay
      label={<span className="uppercase">{t("billing.top_up_label")}</span>}
      aria-label={t("billing.top_up_label")}
      onClose={onClose}
      fill
    >
      <div className="flex-1 flex flex-col w-full pb-13">
        <div data-top-up-intro className="my-4 text-left w-full">
          <p
            className={cx(
              "m-0 font-title text-person font-semibold tracking-headline",
              balance <= 0 ? "text-danger" : "text-ink"
            )}
          >
            {balanceLine(balance)}
          </p>
          <p className="m-0 mt-1 text-note text-ink-soft">{t("billing.carry_over")}</p>
        </div>

        {/* Equal rows: when the best-value badge drops under its hours beside a long price,
            that row is the tallest, and the others match it. */}
        <div role="radiogroup" aria-label={t("billing.top_up_heading")} className="grid auto-rows-fr gap-2 w-full text-left">
          {wallet.packs.map((p) => {
            const selected = p.pack === chosen;
            const hours = packHours(p.credits);
            const bestValue = isBestValue(wallet, p.pack);
            return (
              <button
                key={p.pack}
                type="button"
                role="radio"
                aria-checked={selected}
                data-pack={p.pack}
                // No aria-label: the visible text -- price, credits, hours and what the
                // balance would be -- is the accessible name.
                disabled={busy || !available}
                onClick={() => {
                  setChosen(p.pack);
                  setError("");
                }}
                // The past-session row's box, selected the way the enrolment card shows it
                // is live: the outline goes accent and grows inward, so nothing reflows.
                className={cx(
                  "group min-w-0 flex w-full items-center gap-stack bg-surface border rounded-control py-plate px-gutter text-left",
                  "button-transition enabled:active:button-press disabled:opacity-45",
                  selected
                    ? "border-accent ring-2 ring-accent ring-inset"
                    : "border-line enabled:hover:border-line-strong"
                )}
              >
                {/* Never wrapped or shrunk, so a long local price ("₫530,000") stays whole and
                    the right column gives way. */}
                <span className="flex-none whitespace-nowrap font-title text-hero font-bold tracking-headline text-ink">
                  {packPrice(p.price_minor, p.price_currency)}
                </span>
                <span className="min-w-0 flex-1 flex flex-col items-end gap-list text-right">
                  {/* The credits, at 1.44 times the label size: what the card sells. The
                      number a fifth larger again and bold, on the same baseline. */}
                  <span className="block font-code text-[calc(var(--text-label)*1.44)] leading-(--text-label--line-height) font-medium uppercase tracking-label text-ink">
                    {creditsBefore}
                    <span className="text-[calc(var(--text-label)*1.44*1.2)] font-bold">{credits(p.credits)}</span>
                    {creditsAfter}
                  </span>
                  {/* The hours, quiet under the credits, with the best-value badge just left
                      of them; where they do not fit beside a long price, the reversed wrap
                      drops the badge under the hours. */}
                  {hours === null && !bestValue ? null : (
                    <span className="flex flex-wrap-reverse items-center justify-end gap-x-tight gap-y-list min-h-[calc(var(--text-note)*1.5)]">
                      {bestValue ? <BestValueBadge /> : null}
                      {hours === null ? null : (
                        <span className="text-note text-ink-soft [text-box:trim-both_cap_alphabetic]">{hours}</span>
                      )}
                    </span>
                  )}
                  <span
                    data-pack-after
                    className={cx("block text-note", balance + p.credits > 0 ? "text-ink-soft" : "text-danger")}
                  >
                    {packAfter(balance, p.credits)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <p className="m-0 mt-5 text-note text-ink-soft">{t("billing.run_out_continues")}</p>
        {wallet.upgrade_suggested ? (
          <Button variant="link" className="self-start mt-2" onClick={onComparePlans}>
            {t("plans.compare")}
          </Button>
        ) : null}

        {/* The button, and any checkout error right under it, pinned to the bottom of the
            overlay when the content is taller than the screen. While content scrolls under
            it, it takes the ground's colour and a hairline above. */}
        <div
          className={cx(
            "sticky bottom-0 self-stretch -mx-pad px-pad pt-5 pb-3",
            stuck && "bg-(--bg) border-t border-line"
          )}
        >
          <Button disabled={!pack || busy || !available} onClick={() => void pay()}>
            {busy
              ? t("billing.continuing")
              : pack
                ? t("billing.buy", {
                    credits: credits(pack.credits),
                    price: packPrice(pack.price_minor, pack.price_currency),
                  })
                : t("billing.add_time")}
          </Button>
          {!available || error ? (
            <p role="alert" className="m-0 mt-gutter text-note text-danger min-h-status-line">
              {error || t("billing.unavailable")}
            </p>
          ) : null}
        </div>
        {/* Where the pinned group would rest if it were not pinned: out of view means
            content is scrolling under it. */}
        <div ref={restingPlace} aria-hidden="true" className="self-stretch h-px -mt-px" />
        <div className="grow-2 basis-0" />
      </div>
    </Overlay>
  );
}

/**
 * The best-value badge: the past-sessions status chip's pill, in the accent over its soft
 * tint. The text box is trimmed to the capitals, so equal padding centres them. The right
 * padding gives back the tracking the last letter carries.
 */
function BestValueBadge() {
  return (
    <span className="block flex-none whitespace-nowrap rounded-pill bg-accent-soft py-tight pl-chip-x pr-[calc(var(--spacing-chip-x)-var(--tracking-label))] font-code text-count font-semibold uppercase tracking-label text-accent [text-box:trim-both_cap_alphabetic]">
      {t("billing.pack_best_value")}
    </span>
  );
}

/**
 * A ref for a marker element, and whether that marker is below the bottom of the screen.
 * Put after a `sticky bottom-0` group, the marker shows where the group would rest; out of
 * view below means the group is pinned with content scrolling under it.
 */
export function useOutOfView(): [RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [below, setBelow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      const bottom = entry.rootBounds?.bottom ?? window.innerHeight;
      setBelow(!entry.isIntersecting && entry.boundingClientRect.top >= bottom);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, below];
}
